import { useQuery } from '@tanstack/react-query';
import { useTenantContext } from 'src/context/useTenantContext';
import { NOT_AN_EMPLOYEE, readErrorCode } from 'src/services/apis/errors';
import { tenantEmployeeMe, tenantEmployeeMyIncomes } from 'src/services/apis/tenantRequests';
import { EmployeeEntity, RestaurantBranchEntity, RestaurantEntity } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

/**
 * `GET employees/me/incomes` carries no `@ApiResponse` on the server, exactly like the
 * owner-facing `dashboard/employee-incomes` it delegates to, so codegen emits
 * `SwaggerResponse<any>` for it. Typing it properly needs a server-side response DTO for an
 * object `DashboardService.employeeIncomes` assembles inline, which is a pre-existing gap on
 * both routes and out of scope here — so the shape is named on the client instead, and this
 * declaration is the one place it is written down.
 *
 * Every field is copied from the `result` literal at the end of
 * `apps/server/src/dashboard/dashboard.service.ts` `employeeIncomes()`; nothing here is guessed.
 *
 * The two aggregates are `number`. `sales.totalSales` is `SUM(order.grandTotal)` and
 * `foodAppSales.transactionTotal` is `SUM(transaction.total)`; both underlying columns are
 * `double precision` in the live schema (the entity classes declare plain `number`, but
 * `DATABASE_SYNCHRONIZE=false` and no migration ever created these tables, so the legacy
 * float columns stand). `SUM()` over `double precision` is `double precision`, which
 * node-postgres parses to a JS number — not the string a `bigint` sum would produce.
 *
 * NOTE (scope): every figure below except `employee` is BRANCH-wide for the current
 * calendar month, not per-employee — the query filters on `restaurantId` and
 * `restaurantBranchId` only. Anything rendered from `totalOrders`, `totalProcessingSlowOrders`
 * or `sales` must be labelled as a branch figure, never as "your orders".
 */
export type SelfEmployeeIncomes = {
  employee: EmployeeEntity;
  restaurant: RestaurantEntity;
  restaurantBranch: RestaurantBranchEntity;
  totalOrders: number;
  totalProcessingSlowOrders: number;
  /** `date(order.createdAt)` grouped per day — a Postgres `date`, serialized to an ISO string. */
  sales: { date: string; totalSales: number }[];
  foodAppSales: { transactionDate: string; transactionTotal: number }[];
};

/**
 * A 403 `NOT_AN_EMPLOYEE` is an ANSWER, not a failure: an owner who was never hired
 * legitimately has no employment record. Exported so the branch is testable without a
 * React tree. Reads the code through `readErrorCode`, which handles both the flat
 * `{ code }` body and Nest's nested `{ message: { code } }` shape.
 */
export function isNotAnEmployeeError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status !== 403) return false;
  return readErrorCode(error) === NOT_AN_EMPLOYEE;
}

export type SelfEmployee = {
  employee: EmployeeEntity | undefined;
  incomes: SelfEmployeeIncomes | undefined;
  isLoading: boolean;
  isNotAnEmployee: boolean;
  /** Any failure that is NOT the expected `NOT_AN_EMPLOYEE` answer — a 500, a dead network,
   *  an `INSUFFICIENT_ORG_ROLE` refusal. The page must offer a retry rather than spin. */
  isError: boolean;
  retry: () => void;
};

/**
 * The signed-in user's own employment at the restaurant in scope, plus their income report.
 *
 * Never retried automatically: the only expected error is the 403 above, and retrying it
 * would just re-ask a question that already has an answer. Anything else is surfaced as
 * `isError` with a manual `retry` instead. The incomes query waits for the employment
 * record so a non-employee never fires a second doomed request.
 */
export function useSelfEmployee(): SelfEmployee {
  const { orgSlug, restaurantId } = useTenantContext();

  const employee = useQuery(
    [QUERIES.EMPLOYEE_SELF, orgSlug, restaurantId],
    () => tenantEmployeeMe({ orgSlug, restaurantId }),
    // MainSideNav calls this on every page to decide whether to show the /me entry, and for
    // an owner who was never hired the answer is a 403 that will not change mid-session.
    // Cache it rather than re-asking on every window focus.
    { retry: false, enabled: Boolean(restaurantId), staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  const incomes = useQuery(
    [QUERIES.EMPLOYEE_INCOMES, QUERIES.EMPLOYEE_SELF, orgSlug, restaurantId],
    () => tenantEmployeeMyIncomes<SelfEmployeeIncomes>({ orgSlug, restaurantId }),
    { retry: false, enabled: employee.isSuccess }
  );

  const isNotAnEmployee = isNotAnEmployeeError(employee.error);

  return {
    employee: employee.data,
    incomes: incomes.data,
    isLoading: employee.isLoading && employee.fetchStatus !== 'idle',
    isNotAnEmployee,
    isError: employee.isError && !isNotAnEmployee,
    retry: () => {
      void employee.refetch();
    },
  };
}
